import React, { useContext } from 'react';
import { Button, DatePicker, Dropdown, Modal, Popover } from 'antd';
import {
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FontSizeOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import DefaultLayout from '../../DefaultLayout/DefaultLayout';
import { AppContext } from '../../../App.context';
import useBookingListHook from './BookingList.hook';
import BaseTable from '../../common/BaseTable/BaseTable';
import styles from './BookingList.styles';
import CustomButton from '../../common/CustomButton/CustomButton';
import Colors from '../../../constants/Colors';
import GuestFields from './GuestFields/GuestFields';
import CreateBooking from './CreateBooking/CreateBooking';
import { ReactComponent as AddIcon } from 'assets/images/add.svg';
import { ReactComponent as ExportIcon } from 'assets/images/export.svg';
import apiConfig from '../../../utils/networking/api.config';

export default function BookingList() {
  const {
    appState,
    appActions: { translate, getSelectedHotel },
  } = useContext(AppContext);
  const { state, actions } = useBookingListHook();
  const selectedPms = state.pmsList.find(pms => pms.name === state.selectedHotelPms);
  const { upsellFrontHost, crossellFrontHost, doorOpeningHost } = apiConfig;

  const columns = [
    {
      title: translate('bookingList.locatorCol'),
      dataIndex: 'code',
      key: 'code',
      searchable: true,
      sorter: false,
    },
    {
      title: translate('bookingList.nameAndSurnameCol'),
      dataIndex: 'guest',
      key: 'guest',
    },
    {
      title: translate('hotel'),
      dataIndex: ['hotel', 'name'],
      key: 'hotel',
      width: '12%',
      sorter: false,
    },
    {
      title: translate('bookingList.checkinCol'),
      dataIndex: 'entrance',
      key: 'entrance',
      width: '10%',
      sorter: true,
    },
    {
      title: translate('bookingList.checkoutCol'),
      dataIndex: 'departure',
      key: 'departure',
      width: '10%',
      sorter: true,
    },
    {
      title: translate('bookingList.adultCol'),
      dataIndex: 'adults',
      key: 'adults',
      align: 'center',
      render: (_, row) => {
        return <div>{`${row.completedAdults}/${row.adults}`}</div>;
      },
    },
    {
      title: translate('bookingList.childrenCol'),
      dataIndex: 'children',
      key: 'children',
      align: 'center',
      render: (_, row) => {
        return <div>{`${row.completedChildren}/${row.children}`}</div>;
      },
    },
    {
      title: translate('stats.total'),
      dataIndex: 'total',
      key: 'total',
      align: 'center',
      sorter: false,
      render: (_, row) => {
        const completed = row.completedChildren + row.completedAdults;
        const total = row.children + row.adults + row.babies;

        return (
          <div style={styles.totalContainer}>
            {`${completed || 0}/${total || 0}`}
            <div
              style={{
                ...styles.statusIndicator,
                backgroundColor: actions.getCheckinStatusColor(total, completed),
              }}
            ></div>
          </div>
        );
      },
    },
  ];

  if (state.showNoIntegrationControls) {
    const actionColumn = {
      title: translate('bookingList.actionCol'),
      align: 'center',
      render: (_, row) => {
        const bookingCode = row.code;
        const entranceDate = row.entrance;
        const departureDate = row.departure;

        const today = new Date().toISOString().split('T')[0];

        const items = [
          {
            key: '1',
            disabled: row.status !== 'uncompleted',
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://checkin.civitfun.com/hotel/${
                  getSelectedHotel()?.slug
                }/bookingSearchForm?bookingCode=${bookingCode}&entranceDate=${entranceDate}&autoSearch=true`}
              >
                Checkin
              </a>
            ),
          },
          {
            key: '2',
            disabled: !(row.status === 'checkedin' && departureDate === today),
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://checkout.civitfun.com/hotel/${
                  getSelectedHotel()?.slug
                }/bookingSearchForm?bookingCode=${bookingCode}&departureDate=${departureDate}&autoSearch=true`}
              >
                Checkout
              </a>
            ),
          },
          {
            key: '3',
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://paperless.civitfun.com/hotel/${
                  getSelectedHotel()?.slug
                }/bookingSearchForm?bookingCode=${bookingCode}&entranceDate=${entranceDate}&autoSearch=true&isKiosk=true`}
              >
                Paperless
              </a>
            ),
          },
          {
            key: '4',
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://civitpayment.com/hotel/${
                  getSelectedHotel()?.slug
                }/bookingSearchForm?bookingCode=${bookingCode}&entranceDate=${entranceDate}&autoSearch=true`}
              >
                Payment
              </a>
            ),
          },
          {
            key: '5',
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${upsellFrontHost}rooms?hotelId=${
                  getSelectedHotel()?.slug
                }&bookingCode=${bookingCode}&entranceDate=${entranceDate}`}
              >
                Upselling
              </a>
            ),
          },
          {
            key: '6',
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${crossellFrontHost}items?hotelId=${
                  getSelectedHotel()?.slug
                }&bookingCode=${bookingCode}&entranceDate=${entranceDate}`}
              >
                Cross-selling
              </a>
            ),
          },
          {
            key: '7',
            label: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${doorOpeningHost}hotel/${
                  getSelectedHotel()?.slug
                }/room-info?bookingCode=${bookingCode}&entranceDate=${entranceDate}`}
              >
                Door opening
              </a>
            ),
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button>
              {translate('bookingList.openIn')}
              <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    };

    const statusColumn = {
      title: translate('bookingList.statusCol'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (_, row) => {
        return (
          <div style={{ position: 'relative', width: '100%' }}>
            <select
              defaultValue={row.status}
              disabled={state.editStatus !== row.id}
              onClick={e => {
                const newStatus = e.target.value;
                state.newStatus = newStatus;
              }}
              onChange={e => {
                const newStatus = e.target.value;
                state.newStatus = newStatus;
              }}
              style={styles.statusSelector}
            >
              <option value="checkedin">Checked in</option>
              <option value="uncompleted">Uncompleted</option>
              <option value="checkedout">Checked out</option>
            </select>
          </div>
        );
      },
    };

    columns.splice(8, 0, actionColumn);
    columns.splice(9, 0, statusColumn);
  }

  columns.push({
    title: translate('checkInList.actions'),
    dataIndex: null,
    key: null,
    align: 'center',
    width: '5%',
    render: (_, object) => {
      const viewCheckinContent = <div>{translate('bookingList.actionPop')}</div>;
      const editContent = <div>{translate('bookingList.editPop')}</div>;
      const saveContent = <div>{translate('bookingList.savePop')}</div>;

      const isEditing = state.editStatus === object.id;

      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <Popover content={viewCheckinContent} trigger="hover">
            <Button
              type="secondary"
              icon={<EyeOutlined />}
              onClick={() => actions.onRowClicked(object)}
            />
          </Popover>

          {state.showNoIntegrationControls && (
            <Popover content={isEditing ? saveContent : editContent} trigger="hover">
              <Button
                type="secondary"
                icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
                onClick={() => {
                  if (isEditing) {
                    actions.saveChanges(object.id, state.newStatus);
                  } else {
                    actions.toggleEditStatus(object.id);
                  }
                }}
              />
            </Popover>
          )}
        </div>
      );
    },
  });

  const { RangePicker } = DatePicker;

  return (
    <DefaultLayout singleBox title={translate('bookingList.title')} enableAllHotels={true}>
      <div style={styles.mainContainer}>
        <div style={styles.header}>
          {/* <div style={styles.title}>{translate('checkInList.title')}</div> */}
          <div style={styles.buttonsContainer}>
            <RangePicker
              defaultValue={state.datesRange}
              onChange={actions.onDateChange}
              disabled={state.loading}
            />
            {/* {state.showNoIntegrationControls && (
              <div>
                <CustomButton
                  text={
                    <>
                      <FontSizeOutlined style={{ marginRight: '6px' }} />
                      {translate('bookingList.fields')}
                    </>
                  }
                  background={Colors.grayBackground}
                  onClick={() => {
                    actions.openFieldsModal();
                  }}
                ></CustomButton>
                <Modal
                  open={state.fieldsModal}
                  onCancel={actions.closeFieldsModal}
                  footer={null}
                  width={1037}
                  style={styles.modalFields}
                >
                  <div style={styles.textContainerFields}>
                    <div style={styles.iconFields}>
                      <FontSizeOutlined />
                    </div>
                    <div>
                      <strong style={styles.titleFields}>
                        {translate('bookingList.fieldsView.title')}
                      </strong>
                      <p style={styles.textFields}>
                        {translate('bookingList.fieldsView.description')}
                      </p>
                    </div>
                  </div>
                  <div style={styles.fieldsTable}>
                    <GuestFields
                      hotelId={getSelectedHotel()?.id}
                      translate={translate}
                      selectedPms={selectedPms}
                      onSave={actions.saveGuestFieldsData}
                    />
                  </div>
                  <div style={styles.saveFieldsButton}>
                    <Button
                      type="primary"
                      onClick={() => {
                        actions.saveGuestFieldsData(state.guestFieldsData);
                        actions.closeFieldsModal();
                      }}
                      style={{ width: '95%', borderRadius: '5px' }}
                    >
                      {translate('bookingList.fieldsView.save')}
                    </Button>
                  </div>
                </Modal>
              </div>
            )} */}
            {state.showNoIntegrationControls && (
              <CustomButton
                text={translate('createBooking.create')}
                background={Colors.blueColor}
                color={'white'}
                onClick={actions.onClickCreateBooking}
                disabled={!Boolean(getSelectedHotel()?.id)}
                leftComponent={<AddIcon />}
              ></CustomButton>
            )}
            <CustomButton
              text={translate('bookingList.export')}
              background={Colors.exportButton}
              color={'white'}
              disabled={appState.role === 'guest'}
              onClick={() => {
                actions.export();
              }}
              leftComponent={<ExportIcon />}
            ></CustomButton>
          </div>
        </div>
      </div>

      <div style={styles.layoutContainer}>
        <div style={styles.tableContainer}>
          <BaseTable
            columns={columns}
            data={state.dataSource}
            loading={state.isLoading}
            onChange={filters => actions.onChange(filters)}
          />
        </div>
      </div>
      <CreateBooking
        isOpen={state.createBookingModal}
        onCancel={actions.onCancelCreateBooking}
        onSuccess={actions.onBookingCreated}
        guestMockedData={state.mockedFields}
      />
    </DefaultLayout>
  );
}
